import React, { useEffect, useState } from "react";
import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import {
  getUserProfile,
  updateUserProfile,
  updateUserReportMessage,
  updateUserContractLogo,
} from "../../services/userService";
import {
  User,
  Mail,
  Calendar,
  Shield,
  Settings,
  Edit3,
  Key,
  Lock,
  LogOut,
  FileText,
} from "lucide-react";
import { toast } from "react-toastify";
import ChangePasswordModal from "../Common/ChangePasswordModal";
import "./Perfil.css";
import ContractCustomization from "./Components/ContractCustomization";

const Perfil: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [perfil, setPerfil] = useState<{
    nome: string;
    email: string;
    photoURL?: string;
    createdAt?: Date;
    mensagemRelatorio?: string;
    contractLogoURL?: string;
  } | null>(null);
  const [editandoNome, setEditandoNome] = useState(false);
  const [novoNome, setNovoNome] = useState("");
  const [salvandoNome, setSalvandoNome] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [mensagemRelatorio, setMensagemRelatorio] = useState("");
  const [salvandoMensagem, setSalvandoMensagem] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await getUserProfile();
      if (res.success && res.data) {
        setPerfil(res.data);
        setNovoNome(res.data.nome);
        setMensagemRelatorio(res.data.mensagemRelatorio || "");
      } else setErro(res.error || "Erro ao carregar perfil");
      setLoading(false);
    })();
  }, []);

  const handleSalvarNome = async () => {
    if (!novoNome.trim()) {
      toast.error("Nome não pode estar vazio");
      return;
    }

    setSalvandoNome(true);

    try {
      const res = await updateUserProfile(novoNome);

      if (res.success) {
        // Recarregar o perfil do banco para garantir consistência
        const perfilRes = await getUserProfile();
        if (perfilRes.success && perfilRes.data) {
          setPerfil(perfilRes.data);
        } else {
          // Fallback: atualizar localmente
          setPerfil((prev) => (prev ? { ...prev, nome: novoNome } : null));
        }
        setEditandoNome(false);
        toast.success("Nome atualizado com sucesso!");
      } else {
        toast.error(res.error || "Erro ao atualizar nome");
      }
    } catch (error) {
      toast.error("Erro inesperado ao atualizar nome");
    }

    setSalvandoNome(false);
  };

  const handleCancelarEdicao = () => {
    setNovoNome(perfil?.nome || "");
    setEditandoNome(false);
  };

  const handlePasswordChangeSuccess = async () => {
    // Fazer logout após alteração de senha
    try {
      const { logoutUser } = await import("../../services/authService");
      await logoutUser();
      // Recarregar a página para garantir que o usuário seja redirecionado para login
      window.location.reload();
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
      // Mesmo com erro no logout, recarregar a página
      window.location.reload();
    }
  };

  const handleLogout = async () => {
    try {
      const { logoutUser } = await import("../../services/authService");
      await logoutUser();
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
      toast.error("Erro ao fazer logout");
    }
  };


  const handleSaveContractCustomization = async (
    contractText: string,
    logoFile?: File
  ) => {
    try {
      const msgRes = await updateUserReportMessage(contractText);
      if (!msgRes.success) {
        toast.error(msgRes.error || "Erro ao salvar texto do relatório");
        return;
      }

      setPerfil((prev) => (prev ? { ...prev, mensagemRelatorio: contractText } : prev));

      if (logoFile) {
        const logoRes = await updateUserContractLogo(logoFile);
        if (!logoRes.success) {
          toast.error(logoRes.error || "Erro ao salvar logo");
        } else {
          setPerfil((prev) => (prev ? { ...prev, contractLogoURL: logoRes.data } : prev));
        }
      }

      setShowContractModal(false);
      toast.success("Personalização do contrato salva!");
    } catch (e) {
      toast.error("Erro inesperado ao salvar personalização");
    }
  };

  const formatCreationDate = (date: Date | undefined): string => {
    if (!date) return "Data não disponível";

    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return "Hoje";
    } else if (diffDays === 2) {
      return "Ontem";
    } else if (diffDays <= 7) {
      return `Há ${diffDays - 1} dias`;
    } else {
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    }
  };

  if (loading) {
    return (
      <Container fluid className="p-4">
        <div className="d-flex justify-content-center my-5">
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }

  if (erro) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger" className="my-4">
          {erro}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      {/* Informações da conta */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "16px" }}>
            <Card.Header className="bg-transparent border-0 p-4">
              <h5 className="mb-0 d-flex align-items-center gap-2">
                <Settings size={24} className="text-primary" />
                Informações da Conta
              </h5>
            </Card.Header>
            <Card.Body className="p-4">
              <Row className="align-items-center">
                <Col xs={12} md={3} className="text-center mb-4 mb-md-0">
                  {perfil?.photoURL ? (
                    <img
                      src={perfil.photoURL}
                      alt="Avatar"
                      className="rounded-circle border border-primary border-3"
                      width={120}
                      height={120}
                      style={{ objectFit: "cover" }}
                    />
                  ) : (
                    <div
                      className="rounded-circle bg-primary bg-opacity-10 d-inline-flex align-items-center justify-content-center border border-primary border-3"
                      style={{ width: 120, height: 120 }}
                    >
                      <User size={50} className="text-primary" />
                    </div>
                  )}
                </Col>
                <Col xs={12} md={9}>
                  <Row className="g-3">
                    <Col xs={12} md={6}>
                      <div className="d-flex align-items-center gap-3 p-3 profile-info-card">
                        <div className="flex-grow-1">
                          <h6 className="mb-1 fw-bold text-dark">
                            Nome Completo
                          </h6>
                          {editandoNome ? (
                            <div className="d-flex flex-column">
                              <Form.Control
                                type="text"
                                value={novoNome}
                                onChange={(e) => setNovoNome(e.target.value)}
                                size="sm"
                                className="flex-grow-1"
                              />
                              <div className="d-flex gap-2 mt-2">
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={handleSalvarNome}
                                  disabled={salvandoNome}
                                  className="flex-grow-1 flex-md-grow-0"
                                >
                                  {salvandoNome ? "Salvando..." : "Salvar"}
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={handleCancelarEdicao}
                                  disabled={salvandoNome}
                                  className="flex-grow-1 flex-md-grow-0"
                                >
                                  Cancelar
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2">
                              <p className="mb-0 text-muted flex-grow-1">
                                {perfil?.nome || "Nome não definido"}
                              </p>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setEditandoNome(true)}
                                className="d-flex align-items-center gap-1 w-md-auto"
                              >
                                <Edit3 size={14} />
                                Editar
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div className="d-flex align-items-center gap-3 p-3 profile-info-card">
                        <div
                          className="rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: "40px", height: "40px" }}
                        >
                          <Mail size={20} className="text-success" />
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="mb-1 fw-bold text-dark">E-mail</h6>
                          <p className="mb-0 text-muted text-break">
                            {perfil?.email || "-"}
                          </p>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Status da conta e configurações */}
      <Row className="g-4">
        <Col xs={12} md={6}>
          <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: "16px" }}
          >
            <Card.Header className="bg-transparent border-0 p-4">
              <h6 className="mb-0 d-flex align-items-center gap-2">
                <Shield size={20} className="text-success" />
                Status da Conta
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <div className="d-flex flex-column gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: "32px", height: "32px" }}
                  >
                    <Shield size={16} className="text-success" />
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="mb-1 fw-bold text-dark">Conta Ativa</h6>
                    <p className="mb-0 text-muted small">
                      Sua conta está funcionando normalmente
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle bg-info bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: "32px", height: "32px" }}
                  >
                    <Calendar size={16} className="text-info" />
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="mb-1 fw-bold text-dark">Membro desde</h6>
                    <p
                      className="mb-0 text-muted small"
                      title={
                        perfil?.createdAt
                          ? perfil.createdAt.toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "Data não disponível"
                      }
                    >
                      {formatCreationDate(perfil?.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: "16px" }}
          >
            <Card.Header className="bg-transparent border-0 p-4">
              <h6 className="mb-0 d-flex align-items-center gap-2">
                <Settings size={20} className="text-primary" />
                Configurações
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <div className="d-flex flex-column gap-3">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowPasswordModal(true)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && setShowPasswordModal(true)
                  }
                  className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3 settings-item"
                >
                  <div
                    className="rounded-circle bg-danger bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: "32px", height: "32px" }}
                  >
                    <Lock size={16} className="text-danger" />
                  </div>

                  <div className="flex-grow-1">
                    <h6 className="mb-1 fw-bold text-dark">Alterar Senha</h6>
                    <p className="mb-0 text-muted small">
                      Mantenha sua conta segura
                    </p>
                  </div>
                </div>

                {/* <div className="d-flex flex-column gap-2">
                  <h6 className="mb-1 fw-bold text-dark">Mensagem do Relatório</h6>
                  <p className="mb-0 text-muted small">Texto personalizado impresso nos relatórios (máx. 200 caracteres)</p>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    maxLength={1300}
                    value={mensagemRelatorio}
                    onChange={(e) => setMensagemRelatorio(e.target.value)}
                  />
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">{mensagemRelatorio.length}/1300</small>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSalvarMensagem}
                      disabled={salvandoMensagem}
                      className="d-flex align-items-center gap-1"
                    >
                      {salvandoMensagem ? 'Salvando...' : 'Salvar Mensagem'}
                    </Button>
                  </div>
                </div> */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowContractModal(true)}
                  // onKeyDown={(e) => e.key === "Enter" && handleOpenContractModal()}
                  className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3  contract-item"
                >
                  <div
                    className="rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: "32px", height: "32px" }}
                  >
                    <FileText size={16} className="text-success" />
                  </div>

                  <div className="flex-grow-1">
                    <h6 className="mb-1 fw-bold text-dark">
                      Personalizar Contrato
                    </h6>
                    <p className="mb-0 text-muted small">
                      Defina as regras do contrato e adicione sua identidade
                      visual
                    </p>
                    {(perfil?.mensagemRelatorio || perfil?.contractLogoURL) && (
                      <p className="mb-0 text-success small">
                        {perfil?.mensagemRelatorio ? 'Mensagem definida' : ''}
                        {perfil?.contractLogoURL ? `${perfil?.mensagemRelatorio ? ' • ' : ''}Logo configurada` : ''}
                      </p>
                    )}
                  </div>
                </div>
                {/* <hr className="my-1" /> */}
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3">
                  <div
                    className="rounded-circle bg-warning bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: "32px", height: "32px" }}
                  >
                    <LogOut size={16} className="text-warning" />
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="mb-1 fw-bold text-dark">Sair da Conta</h6>
                    <p className="mb-0 text-muted small">
                      Encerrar sessão atual
                    </p>
                  </div>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={handleLogout}
                    className="d-flex align-items-center gap-1 w-md-auto"
                    style={{
                      borderRadius: "8px",
                      background:
                        "linear-gradient(135deg, #ffc107 0%, #e0a800 100%)",
                      border: "none",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "linear-gradient(135deg, #e0a800 0%, #d39e00 100%)";
                      e.currentTarget.style.transform = "translateY(-1px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        "linear-gradient(135deg, #ffc107 0%, #e0a800 100%)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <LogOut size={14} />
                    Sair
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de Alteração de Senha */}
      <ChangePasswordModal
        show={showPasswordModal}
        onHide={() => setShowPasswordModal(false)}
        onSuccess={handlePasswordChangeSuccess}
      />
      <ContractCustomization
        show={showContractModal}
        onClose={() => setShowContractModal(false)}
        onSave={handleSaveContractCustomization}
        initialText={perfil?.mensagemRelatorio || ""}
        initialLogoURL={perfil?.contractLogoURL || ""}
      />
    </Container>
  );
};

export default Perfil;
